import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./Error404.css";

const Error404 = () => {
  const [scrollY, setScrollY] = useState(0);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [hoveredCard, setHoveredCard] = useState(null);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("mousemove", handleMouseMove);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  const features = [
    { icon: "🚀", title: "Visionary Design", desc: "Cutting-edge interfaces that inspire and convert." },
    { icon: "🧠", title: "Smart Development", desc: "Efficient, scalable, and future-ready code." },
    { icon: "🛠️", title: "Agile Solutions", desc: "Adaptable tech stacks tailored to your business needs." },
    { icon: "🤝", title: "Collaborative Approach", desc: "We build partnerships, not just products." },
  ];

  const techElements = [
    // Web Development
    { symbol: "</>", className: "kr-html", name: "html" },
    { symbol: "⚛️", className: "kr-react", name: "react" },
    { symbol: "{}", className: "kr-node", name: "node" },
    
    // Design
    { symbol: "🎨", className: "kr-design", name: "design" },
    { symbol: "🖌️", className: "kr-paint", name: "paint" },
    { symbol: "📐", className: "kr-ruler", name: "ruler" },
    { symbol: "🖥️", className: "kr-monitor", name: "monitor" },
    { symbol: "📱", className: "kr-mobile", name: "mobile" },
    
    // Startup/Business
    { symbol: "💡", className: "kr-idea", name: "idea" },
    { symbol: "📈", className: "kr-growth", name: "growth" },
    { symbol: "📉", className: "kr-decline", name: "decline" },
    { symbol: "💼", className: "kr-business", name: "business" },
    { symbol: "🤝", className: "kr-partnership", name: "partnership" },
    { symbol: "💰", className: "kr-funding", name: "funding" },
    
    // Data/Charts
    { symbol: "📊", className: "kr-analytics", name: "analytics" },
    { symbol: "📌", className: "kr-pin", name: "pin" },
    { symbol: "🔍", className: "kr-search", name: "search" },
    { symbol: "🔗", className: "kr-link", name: "link" },
    { symbol: "🔄", className: "kr-refresh", name: "refresh" },
    
    // Cloud/DevOps
    { symbol: "☁️", className: "kr-cloud", name: "cloud" },
    { symbol: "🛡️", className: "kr-security", name: "security" },
    { symbol: "🔒", className: "kr-lock", name: "lock" },
    { symbol: "⚡", className: "kr-speed", name: "speed" }
  ];

  return (
    <div className="kr-error-container">
      {/* Floating Particles */}
      <div className="kr-particles">
        {[...Array(20)].map((_, i) => (
          <div 
            key={i} 
            className="kr-particle" 
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              transform: `scale(${Math.random() * 0.5 + 0.5})`
            }}
          />
        ))}
      </div>

      {/* Interactive Light Effect */}
      <div 
        className="kr-light-effect" 
        style={{
          left: `${mousePos.x}px`,
          top: `${mousePos.y}px`,
          transform: `translate(-50%, -50%) scale(${hoveredCard ? 3 : 1})`,
          opacity: hoveredCard ? 0.2 : 0.1
        }}
      />

      <div className="kr-error-content">
        <div className="kr-error-code-container">
          <h1 className="kr-error-code kr-glitch" data-text="404">404</h1>
        </div>
        
        <h2 className="kr-error-title">
          <span className="kr-title-word kr-title-word-1">Lost</span>
          <span className="kr-title-word kr-title-word-2">in</span>
          <span className="kr-title-word kr-title-word-3">Innovation</span>
          <span className="kr-title-word kr-title-word-4">?</span>
        </h2>

        <p className="kr-error-subtitle">
          The page you're seeking has gone off-grid. But don't worry—your next big idea is just a click away.
        </p>

        <Link to="/" className="kr-cta-button">
          <span className="kr-button-text">Return to Home</span>
          <span className="kr-button-hover-effect"></span>
        </Link>
      </div>

      <div className="kr-features-section">
        <h3 className="kr-section-title">
          <span>Why</span>
          <span>choose</span>
          <span>Krinvy?</span>
        </h3>
        
        <div className="kr-feature-cards">
          {features.map((feature, index) => (
            <div 
              key={index}
              className={`kr-card ${hoveredCard === index ? 'kr-active' : ''}`}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div className="kr-card-icon">{feature.icon}</div>
              <h4>{feature.title}</h4>
              <p>{feature.desc}</p>
              <div className="kr-card-underline"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Floating Tech Elements with Random Animations */}
      <div className="kr-tech-elements">
        {techElements.map((element, index) => {
          const top = Math.random() * 80 + 10;
          const left = Math.random() * 80 + 10;
          const animationDuration = Math.random() * 15 + 15;
          const animationDelay = Math.random() * 10;
          const rotateDirection = Math.random() > 0.5 ? 1 : -1;
          const scale = Math.random() * 0.5 + 0.8;
          const animationType = Math.floor(Math.random() * 6);

          return (
            <div 
              key={index}
              className={`kr-tech-element ${element.className}`}
              style={{
                top: `${top}%`,
                left: `${left}%`,
                animation: `
                  kr-float-${animationType} ${animationDuration}s infinite ${animationDelay}s linear,
                  kr-pulse-opacity 3s infinite ease-in-out
                `,
                transform: `scale(${scale})`,
                '--kr-rotate-direction': rotateDirection
              }}
            >
              {element.symbol}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Error404;